import React, { useEffect, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus } from "lucide-react";
import SearchableSelect from "@/components/SearchableSelect";
import DeleteConfirm from "@/components/DeleteConfirm";

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu","Delhi",
  "Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
];

function useRows(url) {
  const [rows, setRows] = useState([]);
  const load = async () => setRows((await api.get(url)).data);
  useEffect(() => { load(); }, [url]);
  return [rows, load];
}

function MasterList({ title, testid, module, url, columns, form, transformCreate, canDelete = true, deleteLabel = (r) => r.name || (r.id || "").slice(0, 8) }) {
  const { can, user } = useAuth();
  const canE = can(module, "edit");
  const [rows, reload] = useRows(url);
  const [f, setF] = useState({ ...form });
  const [editingId, setEditingId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = transformCreate ? transformCreate(f) : f;
      if (editingId) await api.patch(`${url}/${editingId}`, body);
      else await api.post(url, body);
      toast.success("Saved");
      setF({ ...form }); setEditingId(null); reload();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold">{title}</h3>
      </div>

      {canE && (
        <form onSubmit={submit} className="flex flex-wrap gap-3 items-end p-4 bg-white border rounded-lg" data-testid={`${testid}-form`}>
          {Object.entries(form).map(([k]) => (
            k === "type" ? (
              <div key={k} className="min-w-[180px]">
                <Label className="capitalize">{k}</Label>
                <Select value={f[k]} onValueChange={(v) => setF((s) => ({ ...s, [k]: v }))}>
                  <SelectTrigger data-testid={`${testid}-${k}-input`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transporter">Transporter Vehicle</SelectItem>
                    <SelectItem value="company">Company Vehicle</SelectItem>
                    <SelectItem value="customer">Customer Vehicle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div key={k} className="min-w-[200px]">
                <Label className="capitalize">{k}</Label>
                <Input value={f[k]} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} data-testid={`${testid}-${k}-input`} />
              </div>
            )
          ))}
          <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid={`${testid}-save-btn`}>
            <Plus className="h-4 w-4 mr-1" />{editingId ? "Update" : "Add"}
          </Button>
          {editingId && <Button type="button" variant="outline" onClick={() => { setEditingId(null); setF({ ...form }); }}>Cancel</Button>}
        </form>
      )}

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-xs uppercase text-slate-600">
            <tr>
              {columns.map((c) => <th key={c.k} className="px-3 py-2.5 text-left font-semibold">{c.l}</th>)}
              <th className="px-3 py-2.5 text-left font-semibold">Status</th>
              {canE && <th className="px-3 py-2.5 text-left font-semibold">Action</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`${testid}-row-${i}`}>
                {columns.map((c) => <td key={c.k} className="px-3 py-2">{c.fmt ? c.fmt(r[c.k]) : r[c.k]}</td>)}
                <td className="px-3 py-2">
                  <Badge className={r.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>{r.active ? "Active" : "Inactive"}</Badge>
                </td>
                {canE && (
                  <td className="px-3 py-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setEditingId(r.id); setF(Object.fromEntries(Object.keys(form).map((k) => [k, r[k] ?? form[k]]))); }} data-testid={`${testid}-edit-${i}`}>Edit</Button>
                    <Switch checked={r.active} onCheckedChange={async (v) => {
                      const body = Object.fromEntries(Object.keys(form).map((k) => [k, r[k]]));
                      await api.patch(`${url}/${r.id}`, { ...body, active: v });
                      reload();
                    }} data-testid={`${testid}-toggle-${i}`} />
                    {canDelete && user?.role === "admin" && (
                      <DeleteConfirm url={`${url}/${r.id}`}
                        title={`Delete ${title.replace(/s$/, "")}`}
                        description={<div>Delete <b>{deleteLabel(r)}</b> from {title}?</div>}
                        onDeleted={reload}
                        testid={`${testid}-delete-${i}`} iconOnly />
                    )}
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={columns.length + 2} className="text-center py-6 text-slate-500">No records.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Masters() {
  return (
    <div className="space-y-6" data-testid="masters-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Masters</h1>
        <p className="text-slate-500 text-sm mt-1">Manage customers, vehicles, transporters, and view product catalog.</p>
      </div>
      <Tabs defaultValue="customers">
        <TabsList>
          <TabsTrigger value="customers" data-testid="master-tab-customers">Customers</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="master-tab-vehicles">Vehicles</TabsTrigger>
          <TabsTrigger value="transporters" data-testid="master-tab-transporters">Transporters</TabsTrigger>
          <TabsTrigger value="products" data-testid="master-tab-products">Products</TabsTrigger>
        </TabsList>
        <TabsContent value="customers" className="pt-4">
          <CustomersTab />
        </TabsContent>
        <TabsContent value="vehicles" className="pt-4">
          <VehiclesTab />
        </TabsContent>
        <TabsContent value="transporters" className="pt-4">
          <MasterList title="Transporters" testid="transporters" module="transporters" url="/transporters"
            columns={[{ k: "name", l: "Name" }]} form={{ name: "" }} />
        </TabsContent>
        <TabsContent value="products" className="pt-4">
          <ProductCatalog />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CustomersTab() {
  const { can, user } = useAuth();
  const canE = can("customers", "edit");
  const [rows, setRows] = useState([]);
  const empty = { name: "", city: "", state: "", active: true };
  const [f, setF] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => setRows((await api.get("/customers")).data);
  useEffect(() => { load(); }, []);

  const reset = () => { setF(empty); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Customer Name is required");
    if (!f.city.trim()) return toast.error("City is required");
    if (!f.state) return toast.error("State is required");
    try {
      const body = { name: f.name.trim(), city: f.city.trim(), state: f.state, active: f.active };
      if (editingId) await api.patch(`/customers/${editingId}`, body);
      else await api.post("/customers", body);
      toast.success("Saved");
      reset(); load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
  };

  const onEdit = (r) => {
    setEditingId(r.id);
    setF({ name: r.name || "", city: r.city || "", state: r.state || "", active: !!r.active });
  };

  return (
    <div className="space-y-4">
      <h3 className="font-heading text-lg font-semibold">Customers</h3>
      {canE && (
        <form onSubmit={submit} className="p-4 bg-white border rounded-lg space-y-3" data-testid="customers-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Customer Name *</Label>
              <Input value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))}
                placeholder="e.g. Amul Distributor - Delhi" data-testid="customers-name-input" />
            </div>
            <div>
              <Label>City *</Label>
              <Input value={f.city} onChange={(e) => setF((s) => ({ ...s, city: e.target.value }))}
                placeholder="e.g. New Delhi" data-testid="customers-city-input" />
            </div>
            <div>
              <Label>State *</Label>
              <SearchableSelect
                testid="customers-state-select"
                value={f.state}
                onChange={(v) => setF((s) => ({ ...s, state: v }))}
                options={INDIAN_STATES.map((s) => ({ value: s, label: s }))}
                placeholder="Select state / UT"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="customers-save-btn">
              {editingId ? "Update Customer" : "Add Customer"}
            </Button>
            {editingId && <Button type="button" variant="outline" onClick={reset}>Cancel</Button>}
          </div>
        </form>
      )}
      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-xs uppercase text-slate-600">
            <tr>
              {["Customer Name", "City", "State", "Status"].map((h) =>
                <th key={h} className="px-3 py-2.5 text-left font-semibold">{h}</th>)}
              {canE && <th className="px-3 py-2.5 text-left font-semibold">Action</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`customers-row-${i}`}>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2">{r.city || "—"}</td>
                <td className="px-3 py-2">{r.state || "—"}</td>
                <td className="px-3 py-2">
                  <Badge className={r.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>{r.active ? "Active" : "Inactive"}</Badge>
                </td>
                {canE && (
                  <td className="px-3 py-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => onEdit(r)} data-testid={`customers-edit-${i}`}>Edit</Button>
                    <Switch checked={r.active} onCheckedChange={async (v) => {
                      await api.patch(`/customers/${r.id}`, { name: r.name, city: r.city || "", state: r.state || "", active: v });
                      load();
                    }} data-testid={`customers-toggle-${i}`} />
                    {user?.role === "admin" && (
                      <DeleteConfirm url={`/customers/${r.id}`}
                        title="Delete Customer"
                        description={<div>Delete customer <b>{r.name}</b> ({r.city || "—"}, {r.state || "—"})?</div>}
                        onDeleted={load}
                        testid={`customers-delete-${i}`} iconOnly />
                    )}
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={canE ? 5 : 4} className="text-center py-6 text-slate-500">No customers.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VehiclesTab() {
  const { can, user } = useAuth();
  const canE = can("vehicles", "edit");
  const [rows, setRows] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const empty = { number: "", type: "transporter", transporter_id: "", driver_name: "", contact_number: "", active: true };
  const [f, setF] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [v, t] = await Promise.all([api.get("/vehicles"), api.get("/transporters")]);
    setRows(v.data);
    setTransporters(t.data.filter((x) => x.active));
  };
  useEffect(() => { load(); }, []);

  const isCompany = f.type === "company";
  const isTransporter = f.type === "transporter";
  const catLabel = (t) => ({ transporter: "Transporter", company: "Company Transport", customer: "Customer Transport" }[t] || t);

  const reset = () => { setF(empty); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (isTransporter && !f.transporter_id) return toast.error("Transporter Name is required");
    if (!isCompany) {
      if (!f.number.trim()) return toast.error("Vehicle Number is required");
      if (!f.driver_name.trim()) return toast.error("Driver Name is required");
      if (!f.contact_number.trim()) return toast.error("Contact Number is required");
    }
    try {
      const body = {
        type: f.type,
        number: isCompany ? "" : f.number,
        transporter_id: isTransporter ? f.transporter_id : null,
        driver_name: isCompany ? "" : f.driver_name,
        contact_number: isCompany ? "" : f.contact_number,
        active: f.active,
      };
      if (editingId) await api.patch(`/vehicles/${editingId}`, body);
      else await api.post("/vehicles", body);
      toast.success("Saved");
      reset(); load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
  };

  const onEdit = (r) => {
    setEditingId(r.id);
    setF({
      number: r.number || "",
      type: r.type,
      transporter_id: r.transporter_id || "",
      driver_name: r.driver_name || "",
      contact_number: r.contact_number || "",
      active: !!r.active,
    });
  };

  return (
    <div className="space-y-4">
      <h3 className="font-heading text-lg font-semibold">Vehicles</h3>
      {canE && (
        <form onSubmit={submit} className="p-4 bg-white border rounded-lg space-y-3" data-testid="vehicles-form">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label>Transport Category *</Label>
              <Select value={f.type} onValueChange={(v) => setF((s) => ({ ...s, type: v, transporter_id: v === "transporter" ? s.transporter_id : "" }))}>
                <SelectTrigger data-testid="vehicles-type-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transporter">Transporter</SelectItem>
                  <SelectItem value="company">Company Transport</SelectItem>
                  <SelectItem value="customer">Customer Transport</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isTransporter && (
              <div>
                <Label>Transporter Name *</Label>
                <SearchableSelect
                  testid="vehicles-transporter-select"
                  value={f.transporter_id}
                  onChange={(v) => setF((s) => ({ ...s, transporter_id: v }))}
                  options={transporters.map((t) => ({ value: t.id, label: t.name }))}
                  placeholder="Select transporter"
                />
              </div>
            )}
            {!isCompany && (
              <>
                <div>
                  <Label>Vehicle Number *</Label>
                  <Input value={f.number} onChange={(e) => setF((s) => ({ ...s, number: e.target.value }))} placeholder="e.g. DL01AB1234" data-testid="vehicles-number-input" />
                </div>
                <div>
                  <Label>Driver Name *</Label>
                  <Input value={f.driver_name} onChange={(e) => setF((s) => ({ ...s, driver_name: e.target.value }))} data-testid="vehicles-driver-input" />
                </div>
                <div>
                  <Label>Contact Number *</Label>
                  <Input value={f.contact_number} onChange={(e) => setF((s) => ({ ...s, contact_number: e.target.value }))} placeholder="10-digit mobile" data-testid="vehicles-contact-input" />
                </div>
              </>
            )}
          </div>
          {isCompany && (
            <p className="text-xs text-slate-500">
              Company Transport does not require Transporter Name, Vehicle Number, Driver Name, or Contact Number.
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="vehicles-save-btn">
              {editingId ? "Update Vehicle" : "Add Vehicle"}
            </Button>
            {editingId && <Button type="button" variant="outline" onClick={reset}>Cancel</Button>}
          </div>
        </form>
      )}
      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-xs uppercase text-slate-600">
            <tr>
              {["Transport Category", "Transporter Name", "Vehicle Number", "Driver Name", "Contact Number", "Status"].map((h) =>
                <th key={h} className="px-3 py-2.5 text-left font-semibold">{h}</th>)}
              {canE && <th className="px-3 py-2.5 text-left font-semibold">Action</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`vehicles-row-${i}`}>
                <td className="px-3 py-2">{catLabel(r.type)}</td>
                <td className="px-3 py-2">{r.type === "transporter" ? (r.transporter_name || "—") : "—"}</td>
                <td className="px-3 py-2 font-mono-num">{r.type === "company" ? "—" : (r.number || "—")}</td>
                <td className="px-3 py-2">{r.type === "company" ? "—" : (r.driver_name || "—")}</td>
                <td className="px-3 py-2 font-mono-num">{r.type === "company" ? "—" : (r.contact_number || "—")}</td>
                <td className="px-3 py-2">
                  <Badge className={r.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>{r.active ? "Active" : "Inactive"}</Badge>
                </td>
                {canE && (
                  <td className="px-3 py-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => onEdit(r)} data-testid={`vehicles-edit-${i}`}>Edit</Button>
                    <Switch checked={r.active} onCheckedChange={async (v) => {
                      await api.patch(`/vehicles/${r.id}`, {
                        type: r.type, number: r.number || "",
                        transporter_id: r.transporter_id || null,
                        driver_name: r.driver_name || "",
                        contact_number: r.contact_number || "", active: v,
                      });
                      load();
                    }} data-testid={`vehicles-toggle-${i}`} />
                    {user?.role === "admin" && (
                      <DeleteConfirm url={`/vehicles/${r.id}`}
                        title="Delete Vehicle"
                        description={<div>Delete vehicle <b>{r.type === "company" ? "(Company Transport)" : (r.number || "—")}</b>?</div>}
                        onDeleted={load}
                        testid={`vehicles-delete-${i}`} iconOnly />
                    )}
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={canE ? 7 : 6} className="text-center py-6 text-slate-500">No vehicles.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProductCatalog() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/products").then((r) => setData(r.data)); }, []);
  if (!data) return null;
  return (
    <div className="space-y-4">
      {data.products.map((p) => (
        <div key={p.name} className="bg-white border rounded-xl p-5" data-testid={`product-${p.name}`}>
          <h4 className="font-heading font-semibold text-lg">{p.name}</h4>
          <div className="mt-3 grid gap-3">
            {p.brands.map((b) => (
              <div key={b.name} className="border-l-4 border-blue-500 pl-4 py-1">
                <div className="font-medium">{b.name || "No brand"}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  <span>Variants: {b.variants.length ? b.variants.join(", ") : "—"}</span>
                  <span className="mx-2">·</span>
                  <span>Packings: {b.packings.join(", ")}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="text-xs text-slate-500">Product master follows fixed business rules and is not editable from UI.</p>
    </div>
  );
}
